/**
 * Mermaid Flowchart Parser
 * Parses Mermaid flowchart syntax into Flowsage internal node/edge schema.
 * Handles shape-to-nodeType mapping and auto-layout positioning.
 */

import {nanoid} from 'nanoid';
import {hierarchical} from './layoutAlgorithms';

// Mermaid shape → Flowsage nodeType mapping
const SHAPE_MAP={
    'stadium': 'roundedNode',    // ([text])
    'circle': 'circleNode',     // ((text))
    'diamond': 'diamondNode',    // {text}  or rhombus
    'hexagon': 'hexNode',        // {{text}}
    'rect': 'rectNode',       // [text]
    'rounded': 'roundedNode',    // (text)
    'cylinder': 'cylinderNode',   // [(text)]
    'asymmetric': 'arrowNode',      // >text]
    'parallelogram': 'skewRectNode',   // [/text/]
    'parallelogram_alt': 'skewRectNode',   // [\text\]
    'trapezoid': 'trapNode',       // [/text\]
    'trapezoid_alt': 'refTrapNode',    // [\text/]
    'subroutine': 'arrowRectNode',  // [[text]]
    'doublecircle': 'circleNode',    // (((text)))
    'default': 'rectNode',
};

// Direction → edge handle mapping
// Handles: a=top, b=right, c=bottom, d=left
const DIRECTION_HANDLES={
    'TD': {sourceHandle: 'c',targetHandle: 'a'},
    'TB': {sourceHandle: 'c',targetHandle: 'a'},
    'BT': {sourceHandle: 'a',targetHandle: 'c'},
    'LR': {sourceHandle: 'b',targetHandle: 'd'},
    'RL': {sourceHandle: 'd',targetHandle: 'b'},
};

/**
 * Parse a Mermaid node definition and extract id, shape, and text.
 * Supports all standard Mermaid flowchart node shapes.
 *
 * @param {string} raw - Raw node string like `A([Start])` or `B{Decision?}`
 * @returns {{id: string, text: string, shape: string}|null}
 */
// Strip surrounding quotes from parsed text (handles Mermaid's "quoted text" syntax)
function stripQuotes(text) {
    const t=text.trim();
    if((t.startsWith('"')&&t.endsWith('"'))||(t.startsWith("'")&&t.endsWith("'"))) {
        return t.slice(1,-1).trim();
    }
    return t;
}

function parseNodeDef(raw) {
    const s=raw.trim();
    if(!s) return null;

    // Order matters — try most specific patterns first

    // (((text))) → doublecircle
    let m=s.match(/^([A-Za-z0-9_]+)\(\(\((.+?)\)\)\)$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'doublecircle'};

    // ([text]) → stadium
    m=s.match(/^([A-Za-z0-9_]+)\(\[(.+?)\]\)$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'stadium'};

    // ((text)) → circle
    m=s.match(/^([A-Za-z0-9_]+)\(\((.+?)\)\)$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'circle'};

    // {{text}} → hexagon
    m=s.match(/^([A-Za-z0-9_]+)\{\{(.+?)\}\}$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'hexagon'};

    // [[text]] → subroutine
    m=s.match(/^([A-Za-z0-9_]+)\[\[(.+?)\]\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'subroutine'};

    // [(text)] → cylinder
    m=s.match(/^([A-Za-z0-9_]+)\[\((.+?)\)\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'cylinder'};

    // [/text\] → trapezoid
    m=s.match(/^([A-Za-z0-9_]+)\[\/(.+?)\\\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'trapezoid'};

    // [\text/] → trapezoid_alt (inverted)
    m=s.match(/^([A-Za-z0-9_]+)\[\\(.+?)\/\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'trapezoid_alt'};

    // [/text/] → parallelogram
    m=s.match(/^([A-Za-z0-9_]+)\[\/(.+?)\/\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'parallelogram'};

    // [\text\] → parallelogram_alt
    m=s.match(/^([A-Za-z0-9_]+)\[\\(.+?)\\\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'parallelogram_alt'};

    // >text] → asymmetric
    m=s.match(/^([A-Za-z0-9_]+)>(.+?)\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'asymmetric'};

    // {text} → diamond
    m=s.match(/^([A-Za-z0-9_]+)\{(.+?)\}$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'diamond'};

    // (text) → rounded
    m=s.match(/^([A-Za-z0-9_]+)\((.+?)\)$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'rounded'};

    // [text] → rect (must be after all bracket variants)
    m=s.match(/^([A-Za-z0-9_]+)\[(.+?)\]$/);
    if(m) return {id: m[1],text: stripQuotes(m[2]),shape: 'rect'};

    // Bare ID only (no shape defined)
    m=s.match(/^([A-Za-z0-9_]+)$/);
    if(m) return {id: m[1],text: m[1],shape: 'default'};

    return null;
}

/**
 * Parse an edge connection string and extract arrow type + optional label.
 *
 * Supports: -->, --->, -.->  ==>, --- , -.-  ---|text|, -->|text|, -- text -->
 *
 * @param {string} arrow - The arrow portion between two nodes
 * @returns {{edgeType: string, label: string|null}}
 */
function parseArrow(arrow) {
    const a=arrow.trim();
    let label=null;
    let edgeType='simplebezier';

    // Extract label from |text| syntax
    const labelMatch=a.match(/\|(.+?)\|/);
    if(labelMatch) label=labelMatch[1].trim();

    // Extract label from -- text --> syntax
    const inlineLabel=a.match(/^--\s+(.+?)\s+-->$/);
    if(inlineLabel) label=inlineLabel[1].trim();

    // Determine edge type
    if(a.includes('==>')||a.includes('===')) {
        edgeType='step';
    }

    return {edgeType,label};
}

/**
 * Parse a full Mermaid flowchart string into Flowsage internal schema.
 *
 * @param {string} mermaidText - Full Mermaid flowchart text
 * @param {Object} [options={}]
 * @param {boolean} [options.generateNewIds=true] - Generate nanoid IDs (true) or keep Mermaid IDs (false)
 * @returns {{nodes: Array, edges: Array, title: string, direction: string}}
 */
export function parseMermaid(mermaidText,options={}) {
    const {generateNewIds=true}=options;

    const lines=mermaidText.split('\n').map(l => l.trim()).filter(l => l&&!l.startsWith('%%'));
    if(lines.length===0) return {nodes: [],edges: [],title: 'Imported Flowchart',direction: 'TD'};

    // Detect direction from first line
    let direction='TD';
    let startIdx=0;
    const headerMatch=lines[0].match(/^(?:flowchart|graph)\s+(TD|TB|BT|LR|RL)/i);
    if(headerMatch) {
        direction=headerMatch[1].toUpperCase();
        startIdx=1;
    } else if(lines[0].match(/^(?:flowchart|graph)\s*$/i)) {
        startIdx=1;
    }

    // Collect all node definitions and edges
    const nodeDefsMap=new Map(); // mermaidId → {id, text, shape}
    const rawEdges=[];
    const handles=DIRECTION_HANDLES[direction]||DIRECTION_HANDLES['TD'];

    // Parse each content line
    for(let i=startIdx;i<lines.length;i++) {
        const line=lines[i];

        // Skip style/class directives and subgraph keywords
        if(line.startsWith('style ')||line.startsWith('classDef ')||
            line.startsWith('class ')||line.startsWith('click ')||
            line==='end'||line.startsWith('subgraph ')||
            line.startsWith('linkStyle ')) {
            continue;
        }

        // Try to parse as connection line (contains arrow)
        if(line.match(/-->|==>|-\.->|---|-\.-/)) {
            // Split chains: A --> B --> C
            const segments=splitConnectionLine(line,handles);
            for(const seg of segments) {
                // Register node definitions from both sides
                if(seg.leftNode&&!nodeDefsMap.has(seg.leftNode.id)) {
                    nodeDefsMap.set(seg.leftNode.id,seg.leftNode);
                }
                if(seg.rightNode&&!nodeDefsMap.has(seg.rightNode.id)) {
                    nodeDefsMap.set(seg.rightNode.id,seg.rightNode);
                }
                rawEdges.push(seg.edge);
            }
        } else {
            // Standalone node definition
            const nodeDef=parseNodeDef(line);
            if(nodeDef&&!nodeDefsMap.has(nodeDef.id)) {
                nodeDefsMap.set(nodeDef.id,nodeDef);
            }
        }
    }

    // Build ID mapping (Mermaid IDs → Flowsage IDs)
    const idMap=new Map();
    if(generateNewIds) {
        nodeDefsMap.forEach((def) => {
            idMap.set(def.id,nanoid());
        });
    } else {
        nodeDefsMap.forEach((def) => {
            idMap.set(def.id,def.id);
        });
    }

    // Build nodes (without positions — those come from auto-layout)
    const nodesForLayout=[];
    const nodes=[];
    nodeDefsMap.forEach((def) => {
        const nodeId=idMap.get(def.id);
        const nodeType=SHAPE_MAP[def.shape]||'rectNode';
        const node={
            id: nodeId,
            type: nodeType,
            position: {x: 0,y: 0},
            style: {width: 140},
            data: {text: def.text}
        };
        nodes.push(node);
        nodesForLayout.push(node);
    });

    // Build edges
    const edges=rawEdges.map(raw => {
        const sourceId=idMap.get(raw.source);
        const targetId=idMap.get(raw.target);
        if(!sourceId||!targetId) return null;
        return {
            id: `e-${sourceId}-${targetId}`,
            source: sourceId,
            sourceHandle: sourceId+raw.sourceHandle,
            target: targetId,
            targetHandle: targetId+raw.targetHandle,
            type: raw.edgeType,
            ...(raw.label? {label: raw.label}:{})
        };
    }).filter(Boolean);

    // Auto-layout: compute positions using hierarchical algorithm
    const layoutDirection=direction==='BT'? 'BT':direction==='LR'? 'LR':direction==='RL'? 'RL':'TB';
    const edgesForLayout=edges.map(e => ({
        source: e.source,
        target: e.target
    }));
    const positions=hierarchical(nodesForLayout,edgesForLayout,layoutDirection);

    // Apply positions
    positions.forEach(pos => {
        const node=nodes.find(n => n.id===pos.id);
        if(node) {
            node.position={x: pos.position.x,y: pos.position.y};
        }
    });

    return {
        nodes,
        edges,
        title: 'Imported Flowchart',
        direction
    };
}

/**
 * Parse a single connection line, handling chains (A --> B --> C).
 * Uses bracket-depth-aware scanning so node text with spaces is handled correctly.
 * Returns array of {leftNode, rightNode, edge} objects.
 */
function splitConnectionLine(line,handles) {
    const results=[];

    // Step 1: Find arrow positions that are NOT inside bracket delimiters
    let depth=0;
    const arrowPositions=[];

    for(let i=0;i<line.length;i++) {
        const ch=line[i];
        if(ch==='('||ch==='['||ch==='{') depth++;
        else if(ch===')'||ch===']'||ch==='}') depth=Math.max(0,depth-1);

        if(depth>0) continue;

        // Try to match arrow at this position
        const sub=line.slice(i);
        const arrowMatch=sub.match(/^(-->|==>|-\.->|---|-\.-)/);
        if(arrowMatch) {
            // Check for optional |label| after arrow
            const afterArrow=sub.slice(arrowMatch[0].length);
            const labelMatch=afterArrow.match(/^\s*\|([^|]*)\|/);
            const fullEnd=i+arrowMatch[0].length+(labelMatch? labelMatch[0].length:0);

            arrowPositions.push({
                start: i,
                end: fullEnd,
                raw: line.slice(i,fullEnd).trim()
            });
            i=fullEnd-1; // skip past arrow (loop will i++)
        }
    }

    if(arrowPositions.length===0) return results;

    // Step 2: Extract node segments between arrows
    const nodeSegments=[];
    nodeSegments.push(line.slice(0,arrowPositions[0].start).trim());
    for(let i=0;i<arrowPositions.length-1;i++) {
        nodeSegments.push(line.slice(arrowPositions[i].end,arrowPositions[i+1].start).trim());
    }
    nodeSegments.push(line.slice(arrowPositions[arrowPositions.length-1].end).trim());

    // Step 3: Build edge pairs
    for(let i=0;i<arrowPositions.length;i++) {
        const leftNode=parseNodeDef(nodeSegments[i]);
        const rightNode=parseNodeDef(nodeSegments[i+1]);
        const {edgeType,label}=parseArrow(arrowPositions[i].raw);

        if(leftNode&&rightNode) {
            results.push({
                leftNode,
                rightNode,
                edge: {
                    source: leftNode.id,
                    target: rightNode.id,
                    sourceHandle: handles.sourceHandle,
                    targetHandle: handles.targetHandle,
                    edgeType,
                    label
                }
            });
        }
    }

    return results;
}

/**
 * Convert Flowsage internal nodes/edges to Mermaid flowchart syntax.
 * Used for exporting or displaying current state.
 *
 * @param {Array} nodes - Flowsage nodes
 * @param {Array} edges - Flowsage edges
 * @param {string} [direction='TD'] - Flow direction
 * @returns {string} Mermaid flowchart text
 */
export function toMermaid(nodes,edges,direction='TD') {
    if(!nodes||nodes.length===0) return `flowchart ${direction}\n    empty[Empty Flowchart]`;

    // Reverse mapping: nodeType → Mermaid shape wrapper
    const TYPE_TO_SHAPE={
        'rectNode': (id,t) => `${id}[${t}]`,
        'roundedNode': (id,t) => `${id}(${t})`,
        'circleNode': (id,t) => `${id}((${t}))`,
        'diamondNode': (id,t) => `${id}{${t}}`,
        'hexNode': (id,t) => `${id}{{${t}}}`,
        'cylinderNode': (id,t) => `${id}[(${t})]`,
        'arrowNode': (id,t) => `${id}>${t}]`,
        'skewRectNode': (id,t) => `${id}[/${t}/]`,
        'trapNode': (id,t) => `${id}[/${t}\\]`,
        'refTrapNode': (id,t) => `${id}[\\${t}/]`,
        'arrowRectNode': (id,t) => `${id}[[${t}]]`,
        'cloudNode': (id,t) => `${id}((${t}))`,
        'pentNode': (id,t) => `${id}[${t}]`,
        'squircleNode': (id,t) => `${id}(${t})`,
        'crossNode': (id,t) => `${id}[${t}]`,
        'dNode': (id,t) => `${id}(${t})`,
        'tapeNode': (id,t) => `${id}>${t}]`,
        'textNode': (id,t) => `${id}[${t}]`,
        'triangleNode': (id,t) => `${id}[${t}]`,
        'horCylinderNode': (id,t) => `${id}[(${t})]`,
        'diamondRectNode': (id,t) => `${id}{${t}}`,
    };

    // Create short IDs for readability (A, B, C, ... AA, AB, ...)
    const idToShort=new Map();
    const filteredNodes=nodes.filter(n => n.type!=='reccNode');
    filteredNodes.forEach((node,i) => {
        const short=i<26
            ? String.fromCharCode(65+i)
            :String.fromCharCode(65+Math.floor(i/26)-1)+String.fromCharCode(65+(i%26));
        idToShort.set(node.id,short);
    });

    let mermaid=`flowchart ${direction}\n`;

    // Add node definitions
    filteredNodes.forEach(node => {
        const short=idToShort.get(node.id);
        let text=(node.data?.text||'Untitled').replace(/"/g,"'");
        // Quote text containing characters that could break Mermaid shape delimiters
        if(/[[\]{}()|>\\/]/.test(text)) text=`"${text}"`;
        const shapeFormatter=TYPE_TO_SHAPE[node.type]||TYPE_TO_SHAPE['rectNode'];
        mermaid+=`    ${shapeFormatter(short,text)}\n`;
    });

    // Add edge definitions
    if(edges&&edges.length>0) {
        edges.forEach(edge => {
            const sourceShort=idToShort.get(edge.source);
            const targetShort=idToShort.get(edge.target);
            if(!sourceShort||!targetShort) return;

            const arrow=edge.type==='step'? '==>':'-->';
            const label=edge.label? `|${edge.label}|`:'';
            mermaid+=`    ${sourceShort} ${arrow}${label} ${targetShort}\n`;
        });
    }

    return mermaid.trimEnd();
}

/**
 * Validate that a string looks like valid Mermaid flowchart syntax.
 *
 * @param {string} text
 * @returns {{valid: boolean, error: string|null}}
 */
export function validateMermaid(text) {
    if(!text||text.trim().length===0) {
        return {valid: false,error: 'Empty input'};
    }

    const lines=text.trim().split('\n').map(l => l.trim()).filter(l => l);

    // Check for flowchart/graph header
    if(!lines[0].match(/^(flowchart|graph)\s*(TD|TB|BT|LR|RL)?/i)) {
        return {valid: false,error: 'Must start with "flowchart" or "graph" followed by direction (TD, LR, etc.)'};
    }

    // Check there's at least one content line
    if(lines.length<2) {
        return {valid: false,error: 'Flowchart must contain at least one node or connection'};
    }

    return {valid: true,error: null};
}

const mermaidParser={parseMermaid,toMermaid,validateMermaid};
export default mermaidParser;
